import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import type AnalyticsService from '@ebiz/ember-aditools-analytics/services/aditools-analytics';
import type ModelService from '../services/model';
import type PersistenceService from '../services/persistence';
import type RouterService from '@ember/routing/router-service';
import type ToolsService from '../services/toolsapi';
import type LoggingService from '../services/logger';
import {
  sendTabNavigationAnalytics,
  toolbarLinkClickAnalytics,
  loadDesignUnsupportedPartAnalytics,
} from '../utils/analytics';
import type IntlService from 'ember-intl/services/intl';
import type OpampService from '../services/opamp';
import type { SafeString } from '@ember/template/-private/handlebars';

const delay = function (t: number, v?: void | PromiseLike<void>): Promise<void> {
  return new Promise(function (resolve) {
    setTimeout(resolve.bind(null, v), t);
  });
};

interface FeedbackHash {
  email: string;
  name: string;
  feedback: string;
}

interface SystemDialogHash {
  enabled: boolean;
  message: string;
}
interface NotificationDialogHash {
  enabled: boolean;
  title: string | SafeString;
  message: string;
}
const tabNames: Record<string, string> = {
  photodiode: 'Photodiode',
  circuit: 'Circuit Design',
  nextsteps: 'Next Steps',
};

const TABS = ['photodiode', 'circuit', 'nextsteps'];
export default class ApplicationController extends Controller {
  @service declare router: RouterService;
  @service declare model: ModelService;
  @service declare persistence: PersistenceService;
  @service declare toolsapi: ToolsService;
  @service('aditools-site') declare siteconfig: { [key: string]: string };
  @service('aditools-analytics') declare analytics: AnalyticsService;
  @service declare intl: IntlService;
  @service declare logger: LoggingService;
  @service('opamp') declare opampService: OpampService;

  @tracked lang: string | null = null;
  @tracked pdf: string | null = null;

  @tracked enabledTabs = ['photodiode', 'circuit'];
  @tracked visitedTabs = ['photodiode'];
  @tracked tab = 'photodiode';
  @tracked systemDialog: SystemDialogHash = { enabled: false, message: '' };
  @tracked notificationDialog: NotificationDialogHash = {
    enabled: false,
    title: '',
    message: '',
  };

  @tracked safeToSave: (() => boolean)[] = [];

  @action addSaveValidator(item: () => boolean): void {
    this.safeToSave = this.safeToSave.concat([item]);
  }

  get isSafeToSave(): boolean {
    return this.safeToSave.every((fn) => {
      return fn();
    });
  }

  @action isTabDisabled(tabName: string) {
    return !this.enabledTabs.includes(tabName);
  }

  @action selectTab(how: string, tab: string): void {
    const oldTab = this.tab;
    this.tab = tab;
    // Capture required references before async operations
    const router = this.router;
    const systemDialogFunc = this.systemDialogFunc;
    const openNotification = this.openNotification;
    if (tab === 'circuit') {
      console.time('Selecting Circuit Tab to End of Circuit Tab Validation');
      if (this.opampService.opAmps) {
        systemDialogFunc('ON', 'loading-opamp-data');
        this.opampService.opAmps
          .then(async (opamps) => {
            systemDialogFunc('OFF', '');
            if (opamps !== null && opamps.length > 0) {
              router.transitionTo(this.tab);
              sendTabNavigationAnalytics(tabNames[oldTab] as string, tabNames[this.tab] as string, how, this.analytics);
            } else {
              this.tab = oldTab;
              openNotification('dialogs.system.title', 'dialogs.system.error-opamp-data');
            }
          })
          .catch((error) => {
            systemDialogFunc('OFF', '');
            this.tab = oldTab;
            openNotification('dialogs.system.title', 'dialogs.system.error-opamp-data');
            this.logger.log(`Error: ${error}`);
          });
      }
    } else {
      router.transitionTo(this.tab);
      sendTabNavigationAnalytics(tabNames[oldTab] as string, tabNames[this.tab] as string, how, this.analytics);
    }
  }

  @action loadDesign(jsonData: string): Promise<void> {
    return Promise.resolve()
      .then(() => {
        this.systemDialogFunc('ON', 'application-busy');
        return delay(10).then(() => {
          return;
        });
      })
      .then(() => {
        return this.persistence.loadDesign(jsonData);
      })
      .then((result) => {
        this.systemDialogFunc('OFF', '');
        if (result.status !== 0) {
          switch (result.status) {
            case -1:
              this.openNotification('dialogs.load.title', 'dialogs.load.invalid-file');
              break;
            case -2:
              loadDesignUnsupportedPartAnalytics(result.message ? result.message : '', this.analytics);
              this.openNotification('dialogs.load.title', 'dialogs.load.invalid-parts', {
                numParts: result.message?.split(','),
                parts: result.message,
              });
              break;
            case -3:
              this.openNotification('dialogs.load.title', 'dialogs.load.no-opamps');
              break;
          }
          this.logger.log('Error:' + result.message);
        } else {
          this.tab = 'photodiode';
          this.router.transitionTo(this.tab);
          this.enabledTabs = ['photodiode', 'circuit'];
          if (result.updatedChangeSet) {
            // Update the model.state with the load design data
            result.updatedChangeSet.save();
          }
        }
      })
      .catch((error) => {
        this.systemDialogFunc('OFF', '');
        this.openNotification('dialogs.load.title', 'dialogs.load.invalid-file');
        this.logger.log(`Error: ${error}`);
      });
  }

  @action saveDesign(fileName: string, fileDescription: string): void {
    this.persistence.saveDesign(fileName, fileDescription);
    this.logger.log('Save submitted. \nDesign Name: ' + fileName + '\nDesign Description: ' + fileDescription);
  }

  @action sendFeedback(model: FeedbackHash): Promise<void> {
    return Promise.resolve()
      .then(() => {
        this.systemDialogFunc('ON', 'application-busy');
        return delay(10).then(() => {
          return;
        });
      })
      .then(() => {
        return this.toolsapi.deliverFeedback('feedback', model.name, model.email, model.feedback);
      })
      .then((result) => {
        this.systemDialogFunc('OFF', '');
        if (result.status === 0) {
          this.openNotification('dialogs.feedback.title', 'dialogs.feedback.feedback-dialog-ok-message');
        } else {
          this.openNotification('dialogs.feedback.title', 'dialogs.feedback.feedback-dialog-ng-message');
        }
      })
      .catch((error) => {
        this.systemDialogFunc('OFF', '');
        this.openNotification('dialogs.feedback.title', 'dialogs.feedback.feedback-dialog-ng-message');
        this.logger.log(`Error: ${error}`);
      });
  }

  @action systemDialogFunc(status: string, message: string, msgparams?: Record<string, unknown>): void {
    switch (status) {
      case 'ON':
        this.systemDialog = {
          enabled: true,
          message: this.intl.t(`dialogs.system.${message}`, msgparams),
        };
        break;
      case 'OFF':
        this.systemDialog = { enabled: false, message: '' };
        break;
      case 'NONE':
        break;
      default:
        break;
    }
  }

  @action openNotification(title: string, message: string, msgparams?: Record<string, unknown>): void {
    let adjparams = msgparams;
    if (adjparams) {
      adjparams['htmlSafe'] = 'true';
    } else {
      adjparams = { htmlSafe: 'true' };
    }
    this.notificationDialog = {
      enabled: true,
      title: this.intl.t(title, { htmlSafe: true }),
      message: this.intl.t(message, adjparams),
    };
  }

  @action closeNotification(): void {
    this.notificationDialog = {
      enabled: false,
      title: '',
      message: '',
    };
  }

  @action toolbarMenuClick(menuLink: string): void {
    toolbarLinkClickAnalytics(menuLink, tabNames[this.tab] as string, this.analytics);
  }

  @action updateEnabledTabs(tab: string, isInvalid: boolean): void {
    const tablist: string[] = TABS.slice();
    const spliceIndex = tablist.indexOf(tab) + 1;
    if (spliceIndex < tablist.length) {
      tablist.splice(isInvalid ? spliceIndex : spliceIndex + 1);
    }
    for (const [index, tabItem] of tablist.entries()) {
      if (!this.visitedTabs.includes(tabItem) && this.visitedTabs.includes(tablist[index - 1] as string)) {
        this.visitedTabs.push(tabItem);
        break;
      }
    }
    this.enabledTabs = tablist;
  }
}

declare module '@ember/controller' {
  interface Registry {
    application: ApplicationController;
  }
}
